import type { SkillId, StrategyId } from "../model.ts";
import type { Attempt } from "../responses/attempt.ts";
import {
  SKILL_MASTERY_STAGES,
  type SkillMastery,
  type SkillMasteryStage,
} from "../mastery/skill-mastery.ts";
import {
  STRATEGY_MASTERY_STAGES,
  type StrategyMastery,
  type StrategyMasteryStage,
} from "../mastery/strategy-mastery.ts";

export const CARD_TIERS = ["BASE", "FOIL", "HOLO", "GOLD"] as const;
export type CardTier = (typeof CARD_TIERS)[number];

export type AchievementCondition =
  | {
      readonly kind: "ALL";
      readonly conditions: readonly AchievementCondition[];
    }
  | {
      readonly kind: "SKILL_STAGE_AT_LEAST";
      readonly skillId: SkillId;
      readonly stage: SkillMasteryStage;
    }
  | {
      readonly kind: "STRATEGY_STAGE_AT_LEAST";
      readonly strategyId: StrategyId;
      readonly stage: StrategyMasteryStage;
    }
  | {
      readonly kind: "TRANSFER_SUCCESSES";
      readonly sourceSkillId: SkillId;
      readonly successCount: number;
      readonly distinctSessionCount: number;
    };

export interface AchievementCardDefinition {
  readonly id: string;
  readonly achievementId: string;
  readonly collectionNumber: string;
  readonly tier: CardTier;
  readonly title: string;
  readonly earnedCopy: string;
  readonly lockedHint: string | null;
  readonly secret: boolean;
  readonly condition: AchievementCondition;
}

export interface AchievementCatalog {
  readonly version: string;
  readonly cards: readonly AchievementCardDefinition[];
}

export interface AchievementEvaluationSnapshot {
  readonly profileId: string;
  readonly evaluatedAt: string;
  readonly skillMasteryById: ReadonlyMap<SkillId, SkillMastery>;
  readonly strategyMasteryById: ReadonlyMap<StrategyId, StrategyMastery>;
  readonly attempts: readonly Attempt[];
  readonly existingCardIds: ReadonlySet<string>;
  readonly evidenceRefsBySkillId: ReadonlyMap<SkillId, readonly string[]>;
  readonly evidenceRefsByStrategyId: ReadonlyMap<StrategyId, readonly string[]>;
}

export interface AchievementAward {
  readonly key: string;
  readonly id: string;
  readonly profileId: string;
  readonly cardId: string;
  readonly achievementId: string;
  readonly unlockedAt: string;
  readonly evidenceRefs: readonly string[];
  readonly rulesetVersion: string;
}

export interface AchievementCatalogIssue {
  readonly code: string;
  readonly message: string;
  readonly cardId?: string;
}

interface ConditionResult {
  readonly satisfied: boolean;
  readonly evidenceRefs: readonly string[];
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values)].sort();
}

function evaluateCondition(
  condition: AchievementCondition,
  snapshot: AchievementEvaluationSnapshot,
): ConditionResult {
  switch (condition.kind) {
    case "ALL": {
      const results = condition.conditions.map((child) =>
        evaluateCondition(child, snapshot)
      );
      return {
        satisfied: results.every((result) => result.satisfied),
        evidenceRefs: unique(results.flatMap((result) => result.evidenceRefs)),
      };
    }
    case "SKILL_STAGE_AT_LEAST": {
      const mastery = snapshot.skillMasteryById.get(condition.skillId);
      return {
        satisfied: mastery !== undefined
          && SKILL_MASTERY_STAGES.indexOf(mastery.currentStage)
            >= SKILL_MASTERY_STAGES.indexOf(condition.stage),
        evidenceRefs: snapshot.evidenceRefsBySkillId.get(condition.skillId) ?? [],
      };
    }
    case "STRATEGY_STAGE_AT_LEAST": {
      const mastery = snapshot.strategyMasteryById.get(condition.strategyId);
      return {
        satisfied: mastery !== undefined
          && STRATEGY_MASTERY_STAGES.indexOf(mastery.currentStage)
            >= STRATEGY_MASTERY_STAGES.indexOf(condition.stage),
        evidenceRefs: snapshot.evidenceRefsByStrategyId.get(condition.strategyId) ?? [],
      };
    }
    case "TRANSFER_SUCCESSES": {
      const successes = snapshot.attempts.filter((attempt) =>
        attempt.profileId === snapshot.profileId
        && attempt.targetSkillId === condition.sourceSkillId
        && attempt.objective === "TRANSFER"
        && attempt.correct
        && !attempt.helpRequested
        && !attempt.supportUsed
      );
      return {
        satisfied: successes.length >= condition.successCount
          && new Set(successes.map((attempt) => attempt.sessionId)).size
            >= condition.distinctSessionCount,
        evidenceRefs: successes.map((attempt) => attempt.id),
      };
    }
  }
}

export function evaluateAchievements(
  catalog: AchievementCatalog,
  snapshot: AchievementEvaluationSnapshot,
): readonly AchievementAward[] {
  const awards: AchievementAward[] = [];
  for (const card of catalog.cards) {
    if (snapshot.existingCardIds.has(card.id)) continue;
    const result = evaluateCondition(card.condition, snapshot);
    if (!result.satisfied) continue;

    const key = `${snapshot.profileId}::${card.id}`;
    awards.push({
      key,
      id: key,
      profileId: snapshot.profileId,
      cardId: card.id,
      achievementId: card.achievementId,
      unlockedAt: snapshot.evaluatedAt,
      evidenceRefs: unique(result.evidenceRefs),
      rulesetVersion: catalog.version,
    });
  }
  return awards;
}

function validateCondition(
  condition: AchievementCondition,
  cardId: string,
  issues: AchievementCatalogIssue[],
): void {
  if (condition.kind === "ALL") {
    if (condition.conditions.length === 0) {
      issues.push({
        code: "EMPTY_ALL_CONDITION",
        message: "Een ALL-conditie moet minimaal één bewijsvoorwaarde bevatten.",
        cardId,
      });
    }
    for (const child of condition.conditions) validateCondition(child, cardId, issues);
  }
  if (
    condition.kind === "TRANSFER_SUCCESSES"
    && (condition.successCount < 1 || condition.distinctSessionCount < 1)
  ) {
    issues.push({
      code: "INVALID_TRANSFER_THRESHOLD",
      message: "Transferdrempels moeten positief zijn.",
      cardId,
    });
  }
}

export function validateAchievementCatalog(
  catalog: AchievementCatalog,
): readonly AchievementCatalogIssue[] {
  const issues: AchievementCatalogIssue[] = [];
  const ids = new Set<string>();
  const slots = new Set<string>();

  for (const card of catalog.cards) {
    if (ids.has(card.id)) {
      issues.push({
        code: "DUPLICATE_CARD_ID",
        message: `Kaart-ID ${card.id} komt meer dan één keer voor.`,
        cardId: card.id,
      });
    }
    ids.add(card.id);

    const slot = `${card.collectionNumber}.${card.tier}`;
    if (slots.has(slot)) {
      issues.push({
        code: "DUPLICATE_BINDER_SLOT",
        message: `Binder-slot ${slot} komt meer dan één keer voor.`,
        cardId: card.id,
      });
    }
    slots.add(slot);

    if (!card.secret && card.lockedHint === null) {
      issues.push({
        code: "VISIBLE_CARD_WITHOUT_HINT",
        message: "Een zichtbare onverdiende kaart heeft een begrijpelijke hint nodig.",
        cardId: card.id,
      });
    }
    validateCondition(card.condition, card.id, issues);
  }
  return issues;
}
